/* ============================================================================
   O&B collection page behaviour.

   - filter form submits on change, without a full page reload
   - the URL always reflects what is on screen, so a filtered view is
     shareable and the back button works
   - the rail opens as a panel (tablet) or bottom sheet (mobile), with focus
     trapped inside it while open
   - load more appends and reports progress

   Everything degrades: with JS off the form still submits, the load-more
   button is still a real <a> to page 2, and every chip is still a real link.
   ========================================================================= */
(function () {
  'use strict';

  var SECTION = '.ob-collection';
  var root = document.querySelector(SECTION);
  if (!root || root.dataset.obInit === '1') return;
  root.dataset.obInit = '1';

  var form = document.getElementById('ObFilterForm');
  var rail = root.querySelector('[data-ob-rail]');
  var scrim = root.querySelector('.ob-collection__scrim');
  var sortSelect = root.querySelector('[data-ob-sort]');

  /* ---------------------------------------------------------------------
     Fetching a filtered view.
     We request the same URL and swap in the pieces that change, rather than
     reloading. Anything not listed here (the header, the tiles, the title)
     is identical between filtered views and is left alone.
     ------------------------------------------------------------------ */
  var SWAP = [
    '.ob-collection__main',
    '.ob-chips',
    '.ob-toolbar__count',
    '.ob-collection__rail .ob-rail__form',
    '.ob-rail__foot'
  ];

  var inFlight = null;

  function applyUrl(url, pushState) {
    if (inFlight) inFlight.abort();
    var controller = new AbortController();
    inFlight = controller;
    root.classList.add('is-loading');

    fetch(url, { signal: controller.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('bad response');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        // Scope to THIS section in the fetched document. A document-wide
        // lookup is a proven trap on this page - the header carries markup
        // with the same class names and comes first.
        var freshRoot = doc.getElementById(root.id) || doc.querySelector(SECTION) || doc;

        SWAP.forEach(function (sel) {
          var next = freshRoot.querySelector(sel);
          var current = root.querySelector(sel);
          if (next && current) {
            current.replaceWith(next);
          } else if (!next && current) {
            // the chips block disappears entirely when the last filter is cleared
            current.remove();
          } else if (next && !current) {
            var anchor = root.querySelector('.ob-toolbar');
            if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(next, anchor.nextSibling);
          }
        });

        if (pushState) window.history.pushState({ obCollection: true }, '', url);
        root.classList.remove('is-loading');
        inFlight = null;
        bindAll();
        // The swapped-in markup is server-rendered, so its count line reads the
        // full 60 again. Below 1024 only 40 are on screen, so re-correct it
        // after every swap, not only once at init.
        syncInitialProgress();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Never leave the shopper stuck: fall back to a real navigation.
        window.location.href = url;
      });
  }

  function urlFromForm() {
    if (!form) return null;
    var data = new FormData(form);
    var params = new URLSearchParams();
    data.forEach(function (value, key) {
      if (value !== '' && value !== null) params.append(key, value);
    });
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  /* ---------------------------------------------------------------------
     Bindings. Re-run after every swap, because the swapped nodes are new.
     ------------------------------------------------------------------ */
  function bindAll() {
    bindForm();
    bindSort();
    bindChips();
    bindLoadMore();
    bindFacetMore();
    bindDepts();
    bindCats();
    bindPrice();
  }

  /* ---------------------------------------------------------------------
     Price facet (Figma 38:2). Two overlaid range inputs drive the two number
     fields, which are the only named inputs - the sliders carry no name, so
     urlFromForm never sees them and the submitted params stay exactly
     filter.v.price.gte / filter.v.price.lte.

     The two views are one value. Dragging repaints the fill and writes the
     number field live (no request); the request goes out on release, which is
     the range input's own `change`. Typing in a field is debounced by the
     existing 500ms `input` handler on the form, so neither view fires per
     keystroke or per pixel.
     ------------------------------------------------------------------ */
  function bindPrice() {
    var box = root.querySelector('[data-ob-price]');
    if (!box || box.dataset.obBound === '1') return;
    box.dataset.obBound = '1';

    var lo = box.querySelector('[data-ob-price-lo]');
    var hi = box.querySelector('[data-ob-price-hi]');
    var fill = box.querySelector('[data-ob-price-fill]');
    var inLo = document.querySelector('[data-ob-price-in-lo]');
    var inHi = document.querySelector('[data-ob-price-in-hi]');
    if (!lo || !hi || !fill) return;

    var floor = parseFloat(box.dataset.min) || 0;
    var ceil = parseFloat(box.dataset.max) || 0;
    if (ceil <= floor) return;

    function paint() {
      var a = Math.min(parseFloat(lo.value), parseFloat(hi.value));
      var b = Math.max(parseFloat(lo.value), parseFloat(hi.value));
      var span = ceil - floor;
      fill.style.left = ((a - floor) / span) * 100 + '%';
      fill.style.width = ((b - a) / span) * 100 + '%';
    }

    // The handles must not cross; whichever is being dragged pushes the other.
    function clamp(moved) {
      var a = parseFloat(lo.value);
      var b = parseFloat(hi.value);
      if (a > b) {
        if (moved === lo) { hi.value = a; } else { lo.value = b; }
      }
    }

    function sync(moved) {
      clamp(moved);
      paint();
      if (inLo) inLo.value = lo.value;
      if (inHi) inHi.value = hi.value;
    }

    [lo, hi].forEach(function (el) {
      el.addEventListener('input', function () { sync(el); });
      el.addEventListener('change', function () {
        sync(el);
        var url = urlFromForm();
        if (url) applyUrl(url, true);
      });
    });

    // Typing in a field moves the matching handle. The form's own debounced
    // `input` handler still owns the request, so this only keeps the views level.
    [[inLo, lo], [inHi, hi]].forEach(function (pair) {
      if (!pair[0]) return;
      pair[0].addEventListener('input', function () {
        var v = parseFloat(pair[0].value);
        if (isNaN(v)) return;
        pair[1].value = Math.min(Math.max(v, floor), ceil);
        clamp(pair[1]);
        paint();
      });
    });

    paint();
  }

  // Keeps the count line in step with what is on screen. The progress rail it
  // also drove was removed by decision on 30 August 2026; the words carry it.
  function updateProgress(shown, total) {
    var shownEl = root.querySelector('[data-ob-progress-shown]');
    if (shownEl) shownEl.textContent = shown.toLocaleString('en-GB');
  }

  // Department groups inside the Category facet.
  function bindDepts() {
    root.querySelectorAll('[data-ob-dept-toggle]').forEach(function (btn) {
      if (btn.dataset.obBound === '1') return;
      btn.dataset.obBound = '1';
      btn.addEventListener('click', function () {
        var dept = btn.closest('.ob-dept');
        if (!dept) return;
        var open = dept.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    root.querySelectorAll('[data-ob-sizemore]').forEach(function (btn) {
      if (btn.dataset.obBound === '1') return;
      btn.dataset.obBound = '1';
      btn.addEventListener('click', function () {
        var chips = btn.parentNode.querySelector('[data-ob-sizegroup]');
        if (!chips) return;
        var open = chips.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Show less' : 'Show more';
      });
    });
  }

  // Shop all categories carousel. Steps by whole tiles, clamps at both ends,
  // and disables an arrow rather than letting it run past the track.
  function bindCats() {
    var root2 = document.querySelector('[data-ob-cats]');
    if (!root2 || root2.dataset.obBound === '1') return;
    root2.dataset.obBound = '1';

    var vp = root2.querySelector('[data-ob-cats-viewport]');
    var track = root2.querySelector('[data-ob-cats-track]');
    var prev = root2.querySelector('[data-ob-cats-prev]');
    var next = root2.querySelector('[data-ob-cats-next]');
    if (!vp || !track) return;
    var offset = 0;

    function step() {
      var tile = track.querySelector('.ob-cats__tile');
      if (!tile) return 240;
      return tile.getBoundingClientRect().width + 16;
    }

    function maxOffset() {
      return Math.max(0, track.scrollWidth - vp.clientWidth);
    }

    function apply() {
      var max = maxOffset();
      // Nothing overflows: no arrows, no clipping.
      root2.classList.toggle('is-static', max <= 1);
      offset = Math.min(Math.max(0, offset), max);
      track.style.transform = 'translateX(' + -offset + 'px)';
      if (prev) prev.disabled = offset <= 0;
      if (next) next.disabled = offset >= max - 1;
    }

    if (next) next.addEventListener('click', function () { offset += step(); apply(); });
    if (prev) prev.addEventListener('click', function () { offset -= step(); apply(); });
    window.addEventListener('resize', apply);
    if (document.readyState === 'complete') apply();
    else window.addEventListener('load', apply);
    apply();
  }

  function bindFacetMore() {
    root.querySelectorAll('[data-ob-facet-more]').forEach(function (btn) {
      if (btn.dataset.obBound === '1') return;
      btn.dataset.obBound = '1';
      // The collapsed label is built in Liquid from the real total - "Show all
      // 17 colours", "Show all 13 in Men's" (Figma 244:2 / 47:2). Stash it on
      // first use; overwriting it with a generic string threw the count away and
      // the control never came back with the right words on the second click.
      btn.dataset.obLabel = btn.dataset.obLabel || btn.textContent.trim();
      btn.addEventListener('click', function () {
        // Size caps PER GROUP, so a control inside a group must expand only
        // that group. Both size groups share one .ob-facet__body, so scoping to
        // the body would have expanded Clothing and Footwear together from
        // either button.
        var body = btn.closest('.ob-sizegroup') || btn.closest('.ob-facet__body');
        if (!body) return;
        var open = body.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Show less' : btn.dataset.obLabel;
      });
    });
  }

  function bindForm() {
    var f = document.getElementById('ObFilterForm');
    if (!f || f.dataset.obBound === '1') return;
    f.dataset.obBound = '1';
    form = f;

    f.addEventListener('change', function () {
      var url = urlFromForm();
      if (url) applyUrl(url, true);
    });

    // A price range should not fire a request on every keystroke.
    var debounce;
    f.addEventListener('input', function (e) {
      if (e.target.type !== 'number') return;
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        var url = urlFromForm();
        if (url) applyUrl(url, true);
      }, 500);
    });

    f.addEventListener('submit', function (e) {
      e.preventDefault();
      var url = urlFromForm();
      if (url) applyUrl(url, true);
    });
  }

  function bindSort() {
    var sel = root.querySelector('[data-ob-sort]');
    if (!sel || sel.dataset.obBound === '1') return;
    sel.dataset.obBound = '1';
    sortSelect = sel;

    sel.addEventListener('change', function () {
      // Featured has an empty value: no sort_by, so the collection's own
      // default order is served.
      var params = new URLSearchParams(window.location.search);
      if (sel.value) { params.set('sort_by', sel.value); } else { params.delete('sort_by'); }
      var qs = params.toString();
      applyUrl(window.location.pathname + (qs ? '?' + qs : ''), true);
    });
  }

  function bindChips() {
    var chips = root.querySelector('[data-ob-chips]');
    if (!chips || chips.dataset.obBound === '1') return;
    chips.dataset.obBound = '1';

    var more = chips.querySelector('[data-ob-chips-more]');
    if (more) {
      var label = more.querySelector('[data-ob-chips-more-label]');
      var original = label ? label.textContent : '';
      more.addEventListener('click', function () {
        var open = chips.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) label.textContent = open ? 'Show less' : original;
      });
    }

    // Where focus goes after the chip it was on is removed. Left alone it
    // lands on <body>, which strands a keyboard user at the top of the
    // document with no idea what happened. Prefer the next chip, then the
    // previous one, then Clear all, then the count line - always something
    // adjacent to what was just removed.
    function restoreFocus(removed) {
      var live = root.querySelector('[data-ob-chips]');
      var target = null;
      if (live) {
        var list = live.querySelectorAll('a.ob-chip:not(.ob-chip--more)');
        if (list.length) {
          target = list[Math.min(removed, list.length - 1)];
        } else {
          target = live.querySelector('.ob-chips__clear');
        }
      }
      if (!target) {
        target = root.querySelector('[data-ob-progress], .ob-toolbar__count');
        if (target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      }
      if (target) target.focus({ preventScroll: true });
    }

    function activate(link) {
      var list = [].slice.call(chips.querySelectorAll('a.ob-chip:not(.ob-chip--more)'));
      var idx = list.indexOf(link);
      applyUrl(link.getAttribute('href'), true);
      // applyUrl swaps .ob-chips, so wait for the new markup before focusing
      window.setTimeout(function () { restoreFocus(idx < 0 ? 0 : idx); }, 350);
    }

    chips.addEventListener('click', function (e) {
      var link = e.target.closest('a.ob-chip, .ob-chips__clear');
      if (!link) return;
      e.preventDefault();
      activate(link);
    });

    // An <a href> activates on Enter but NOT on Space - that is the anchor's
    // own behaviour, not a bug we introduced, but the chip reads as a button
    // so both keys must work.
    chips.addEventListener('keydown', function (e) {
      if (e.key !== ' ' && e.key !== 'Spacebar') return;
      var link = e.target.closest('a.ob-chip, .ob-chips__clear');
      if (!link) return;
      e.preventDefault();
      activate(link);
    });
  }

  // The product grid inside a given scope. Never document-wide: the header
  // search panel uses .ob-card-grid too and appears earlier in the DOM.
  function gridIn(scope) {
    if (!scope) return null;
    return scope.querySelector('.ob-card-grid.small--grid--flush') ||
           scope.querySelector('.collection-grid__wrapper .ob-card-grid') ||
           scope.querySelector('.collection-grid__wrapper .grid');
  }

  function bindLoadMore() {
    var btn = root.querySelector('.ob-load-more');
    if (!btn || btn.dataset.obBound === '1') return;
    btn.dataset.obBound = '1';

    btn.addEventListener('click', function (e) {
      var url = btn.getAttribute('data-next-url');
      if (!url) return;
      e.preventDefault();
      if (btn.classList.contains('is-loading')) return;

      // Below 1024 the last 20 of the 60 already on the page are held back by
      // CSS. Reveal those before fetching anything - they are already here.
      var grid0 = gridIn(root);
      if (grid0 && !grid0.classList.contains('ob-revealed') &&
          window.matchMedia('(max-width: 1023px)').matches &&
          grid0.children.length > 40) {
        grid0.classList.add('ob-revealed');
        updateProgress(grid0.children.length, null);
        return;
      }

      // (d) loading state: the button cannot fire twice, and skeletons hold
      // the space the incoming cards will occupy so nothing shifts on arrival.
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
      grid0 = gridIn(root);
      var skeletons = [];
      if (grid0) {
        var per = 60;
        for (var si = 0; si < per; si++) {
          var sk = document.createElement('div');
          sk.className = 'ob-card ob-card--skeleton';
          sk.setAttribute('aria-hidden', 'true');
          grid0.appendChild(sk);
          skeletons.push(sk);
        }
      }
      var clearSkeletons = function () {
        skeletons.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
        skeletons = [];
      };

      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var grid = gridIn(root);
          // The fetched document also contains the HEADER search grid, and it
          // comes first - so a document-wide .ob-card-grid lookup returns three
          // search results instead of sixty products. Scope to this section.
          var freshRoot = doc.getElementById(root.id) || doc;
          var freshGrid = gridIn(freshRoot);
          if (grid && freshGrid) {
            clearSkeletons();
            Array.prototype.forEach.call(freshGrid.children, function (node) {
              var clone = node.cloneNode(true);
              clone.classList.add('aos-animate');
              clone.classList.add('ob-card--entering');
              grid.appendChild(clone);
            });
            // let the class paint, then release it so the fade runs
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                grid.querySelectorAll('.ob-card--entering').forEach(function (n) {
                  n.classList.remove('ob-card--entering');
                });
              });
            });
          }

          var freshBtn = doc.querySelector('.ob-load-more');
          var next = freshBtn ? freshBtn.getAttribute('data-next-url') : '';
          if (freshBtn && freshBtn.getAttribute('data-ob-loaded')) {
            updateProgress(parseInt(freshBtn.getAttribute('data-ob-loaded'), 10),
                           parseInt(btn.getAttribute('data-ob-total'), 10));
          }

          if (next) {
            btn.setAttribute('data-next-url', next);
            btn.setAttribute('href', next);
            btn.setAttribute('data-ob-loaded', freshBtn.getAttribute('data-ob-loaded') || '');
          } else {
            // Everything is loaded: the button goes, the rail reads full.
            var totalN = parseInt(btn.getAttribute('data-ob-total'), 10) || 0;
            var line = root.querySelector('[data-ob-progress]');
            if (line) line.textContent = 'Showing all ' + totalN.toLocaleString('en-GB') + ' products';
            btn.remove();
          }
          clearSkeletons();
          btn.classList.remove('is-loading');
          btn.setAttribute('aria-busy', 'false');
          btn.removeAttribute('aria-disabled');
        })
        .catch(function () { window.location.href = url; });
    });
  }

  /* ---------------------------------------------------------------------
     The rail as a panel / sheet, with a focus trap.
     ------------------------------------------------------------------ */
  var lastFocused = null;

  function focusable() {
    if (!rail) return [];
    return Array.prototype.filter.call(
      rail.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
  }

  function openRail() {
    if (!rail) return;
    lastFocused = document.activeElement;
    rail.classList.add('is-open');
    if (scrim) scrim.hidden = false;
    document.body.classList.add('ob-rail-open');
    var trigger = root.querySelector('[data-ob-rail-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var first = focusable()[0];
    if (first) first.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closeRail() {
    if (!rail) return;
    rail.classList.remove('is-open');
    if (scrim) scrim.hidden = true;
    document.body.classList.remove('ob-rail-open');
    var trigger = root.querySelector('[data-ob-rail-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      closeRail();
      return;
    }
    if (e.key !== 'Tab') return;
    var items = focusable();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-ob-rail-open]')) {
      e.preventDefault();
      openRail();
    } else if (e.target.closest('[data-ob-rail-close]')) {
      e.preventDefault();
      closeRail();
    }
  });

  window.addEventListener('popstate', function () {
    applyUrl(window.location.pathname + window.location.search, false);
  });

  // Below 1024 the last 20 of the 60 on the page are held back by CSS, so the
  // server-rendered "Showing 60" would overstate what is actually on screen.
  function syncInitialProgress() {
    var grid = root.querySelector('.ob-card-grid');
    if (!grid) return;
    if (window.matchMedia('(max-width: 1023px)').matches &&
        !grid.classList.contains('ob-revealed')) {
      var visible = Math.min(40, grid.querySelectorAll('.ob-card').length);
      if (visible) updateProgress(visible, null);
    }
  }

  // The sticky rail must clear the WHOLE chrome, not just the header: the
  // announcement bar sits above it and both change height across breakpoints,
  // so a single hard-coded offset is wrong at two of the three.
  function measureChrome() {
    var ann = document.querySelector('.announcement-bar');
    var hdr = document.querySelector('.site-header');
    var chrome = (ann ? ann.offsetHeight : 0) + (hdr ? hdr.offsetHeight : 0);
    if (chrome > 0) {
      document.documentElement.style.setProperty('--ob-chrome-h', chrome + 'px');
    }
  }

  measureChrome();
  window.addEventListener('resize', measureChrome);
  window.addEventListener('load', measureChrome);

  bindAll();
  syncInitialProgress();
})();
